import { useState, useEffect, useRef, useCallback } from 'react';

const useWebSocket = (url, options = {}) => {
  const {
    reconnect = true,
    reconnectInterval = 1000,
    maxReconnectInterval = 30000,
    maxReconnectAttempts = 10,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);
  const ws = useRef(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimeout = useRef(null);
  const isNetworkError = useRef(false);

  const connect = useCallback(() => {
    try {
      // Check that the URL is valid
      if (!url || url.trim() === '') {
        console.warn('WebSocket: URL vide ou invalide');
        return;
      }

      // Ne pas reconnecter si hors ligne
      if (!navigator.onLine) {
        console.log('WebSocket: Offline, waiting for network reconnection');
        isNetworkError.current = true;
        return;
      }

      // Nettoyer la connexion existante
      if (ws.current) {
        ws.current.close();
      }

      console.log('WebSocket: Attempting connection to', url);
      ws.current = new WebSocket(url);

      ws.current.onopen = () => {
        console.log('✅ WebSocket connected:', url);
        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0;
        isNetworkError.current = false;
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);
        } catch (err) {
          console.error('WebSocket: Erreur parsing message:', err);
        }
      };

      ws.current.onerror = (err) => {
        console.error('❌ WebSocket erreur:', err);
        // Detect network errors (ERR_NETWORK_CHANGED, etc.)
        if (!navigator.onLine) {
          isNetworkError.current = true;
          setError('Network error - automatic reconnection');
        } else {
          setError('Erreur de connexion WebSocket');
        }
      };

      ws.current.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);

        // Tentative de reconnexion avec backoff exponentiel
        if (
          reconnect &&
          reconnectAttempts.current < maxReconnectAttempts &&
          event.code !== 1000 // 1000 = fermeture normale
        ) {
          reconnectAttempts.current += 1;

          // Exponential backoff: 1s, 2s, 4s, 8s... up to maxReconnectInterval
          const delay = Math.min(
            reconnectInterval * Math.pow(2, reconnectAttempts.current - 1),
            maxReconnectInterval
          );

          console.log(
            `WebSocket: Reconnexion dans ${delay}ms (tentative ${reconnectAttempts.current}/${maxReconnectAttempts})`
          );

          reconnectTimeout.current = setTimeout(() => {
            connect();
          }, delay);
        } else if (reconnectAttempts.current >= maxReconnectAttempts) {
          setError('Nombre maximum de tentatives de reconnexion atteint');
        }
      };
    } catch (err) {
      console.error('WebSocket: Error during creation:', err);
      setError(err.message);
    }
  }, [url, reconnect, reconnectInterval, maxReconnectInterval, maxReconnectAttempts]);

  // Network reconnection handler
  const handleOnline = useCallback(() => {
    console.log('WebSocket: Network restored, reconnecting...');
    // Reset attempts on network change
    if (isNetworkError.current) {
      reconnectAttempts.current = 0;
      isNetworkError.current = false;
    }
    // Reconnect if not already connected
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      connect();
    }
  }, [connect]);

  const handleOffline = useCallback(() => {
    console.log('WebSocket: Network lost');
    isNetworkError.current = true;
    setError('Network connection lost');
  }, []);

  useEffect(() => {
    if (!url) {
      console.warn('WebSocket: URL non fournie, pas de connexion');
      return;
    }

    connect();

    // Listen to network events to handle ERR_NETWORK_CHANGED
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      if (ws.current) {
        // Code 1000 = fermeture normale, pas de reconnexion
        ws.current.close(1000, 'Component unmounted');
      }
    };
  }, [url, connect, handleOnline, handleOffline]);

  const sendMessage = useCallback((message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
      return true;
    } else {
      console.warn('WebSocket: Impossible d\'envoyer, connexion non ouverte');
      return false;
    }
  }, []);

  const disconnect = useCallback(() => {
    if (ws.current) {
      ws.current.close(1000, 'Manual disconnect');
    }
  }, []);

  return { 
    isConnected, 
    lastMessage, 
    error,
    sendMessage,
    disconnect,
  };
};

export default useWebSocket;
